import { useEffect, useState } from 'react'
import { useLocation, Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RutInput } from '@/components/RutInput'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ShieldCheck, ArrowRight, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { NotSuperAdminError, useUser } from '@/contexts/UserContext'
import { ApiError, LOGOUT_MENSAJE_KEY } from '@/lib/api'
import { validateRut } from '@/lib/rut'

export function LoginPage() {
  const [, navigate] = useLocation()
  const { login } = useUser()
  const [rut, setRut] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showRutError, setShowRutError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // El 401 revocado deja el motivo en sessionStorage y redirige con
  // `window.location.href` (navegación completa): sonner no sobrevive al
  // desmontaje, así que el toast se muestra acá, ya montada la pantalla de
  // destino. Se consume una sola vez — un reload posterior no debe repetirlo.
  useEffect(() => {
    const mensaje = sessionStorage.getItem(LOGOUT_MENSAJE_KEY)
    if (!mensaje) return
    sessionStorage.removeItem(LOGOUT_MENSAJE_KEY)
    toast.error(mensaje, { duration: 8000 })
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!rut || !password) {
      toast.error('Por favor completa todos los campos')
      return
    }

    if (!validateRut(rut)) {
      setShowRutError(true)
      toast.error('El RUT no es válido')
      return
    }

    setIsLoading(true)

    try {
      await login(rut, password)
      toast.success('¡Sesión iniciada correctamente!')
      navigate('/')
    } catch (err) {
      if (err instanceof NotSuperAdminError) {
        toast.error(err.message)
      } else if (err instanceof ApiError) {
        if (err.status === 422 || err.status === 401) {
          toast.error('Credenciales incorrectas. Verifica tu RUT y contraseña.')
        } else if (err.status === 403) {
          toast.error('Esta cuenta no tiene acceso al panel de administración.')
        } else {
          toast.error('Ocurrió un error. Intenta nuevamente.')
        }
      } else {
        toast.error('No se pudo conectar con el servidor.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-chart-2 text-primary-foreground shadow-md mx-auto mb-4">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Facil Control</h1>
          <p className="text-muted-foreground mt-2">Panel de Super Administración</p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="space-y-2 pb-6">
            <CardTitle className="text-2xl">Inicia sesión</CardTitle>
            <CardDescription>Acceso restringido a administradores de la plataforma</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rut" className="text-sm font-medium">
                  RUT
                </Label>
                <RutInput
                  id="rut"
                  placeholder="12.345.678-9"
                  value={rut}
                  onChange={(value) => {
                    setRut(value)
                    if (showRutError) setShowRutError(false)
                  }}
                  validate
                  showError={showRutError}
                  className="h-11"
                  autoComplete="username"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Contraseña
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10"
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={isLoading} className="w-full h-11 gap-2 mt-6">
                {isLoading ? (
                  <div className="h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin" />
                ) : (
                  <>
                    Iniciar Sesión
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link href="/">
                <a className="text-primary hover:underline font-medium">Volver al inicio</a>
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-8">
          © {new Date().getFullYear()} Facil Control. Panel interno.
        </p>
      </div>
    </div>
  )
}
